// @ts-ignore
import expandMore from 'material-icons-svg/icons/baseline-add_circle-24px.svg';
// @ts-ignore
import expandLess from 'material-icons-svg/icons/baseline-remove_circle-24px.svg';

import React from 'react';
import {
  mxCell, mxCellState, mxConstants, mxEdgeStyle, mxGraphLayout, mxImage as MxImage, mxRectangle as MxRectangle,
} from 'mxgraph-js';
import HCLTreeMxGraph, { HCLTreeMxGraphJSON } from './HCLTreeMxGraph';
import FaultTreeMxGraphDecoder from '../HCLTreeMxGraphDecoder/FaultTreeMxGraphDecoder';
import FaultTreeMxGraphEncoder from '../HCLTreeMxGraphEncoder/FaultTreeMxGraphEncoder';
import FaultTreeMxGraphLayout from '../HCLTreeMxGraphLayout/FaultTreeMxGraphLayout';
import ReferenceTypes from '../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes';
import { BasicEventVertexValueJSON } from '../HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertexValue';
import { HouseEventVertexValueJSON } from '../HCLTreeNode/FaultTreeNode/HouseEventNode/HouseEventVertexValue';
import { GateVertexValueJSON } from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertexValue';
import { OutcomeJSON } from '../HCLTreeNode/HCLTreeVertexValue/Outcome';
import FaultTreeMxStylesheet from '../Stylesheet/FaultTreeMxStylesheet';
import FaultTreeMxCellRenderer from '../HCLCellRenderer/FaultTreeMxCellRenderer';
import HCLTreeVertex from '../HCLTreeNode/HCLTreeVertex';
import FaultTreeVertex from '../HCLTreeNode/FaultTreeNode/FaultTreeVertex';
import GateVertex from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertex';
import FaultTreeMxGraphModel from '../HCLMxGraphModel/FaultTreeMxGraphModel';
import FaultTreeMxGraphSelectionModel from "../HCLMxGraphSelectionModel/FaultTreeMxGraphSelectionModel";

export interface FaultTreeMxGraphJSON extends HCLTreeMxGraphJSON {
  [ReferenceTypes.BASIC_EVENTS]: {[index: string]: BasicEventVertexValueJSON};
  [ReferenceTypes.HOUSE_EVENTS]: {[index: string]: HouseEventVertexValueJSON};
  [ReferenceTypes.GATES]: {[index: string]: GateVertexValueJSON};
  components: {[index: string]: any};
  top_node: OutcomeJSON;
}

class FaultTreeMxGraph extends HCLTreeMxGraph {
  private containerRef?: React.RefObject<unknown>;
  private commonCause?: any;
  private commonCauseFactors?: any;

  /**
   * @param {() => void} onGraphMounted
   * @param {() => void} onGraphUnmounted
   * @param {React.RefObject} containerRef?
   * @param {any} commonCause?
   */
  constructor(onGraphMounted: () => void, onGraphUnmounted: () => void, containerRef?: React.RefObject<unknown>, commonCause?: any, commonCauseFactors?: any) {
    super(onGraphMounted, onGraphUnmounted, new FaultTreeMxGraphModel());
    this.containerRef = containerRef;
    this.commonCause = commonCause;
    this.commonCauseFactors = commonCauseFactors;
  }

  /**
   * @return {React.RefObject}
   */
  getContainerRef(): React.RefObject<unknown> | undefined {
    return this.containerRef;
  }

  public createCellRenderer(): FaultTreeMxCellRenderer {
    return new FaultTreeMxCellRenderer();
  }

  /**
   * @override
   * @param {MxGraphType.mxCellState} state
   * @return {MxGraphType.mxRectangle}
   */
  getVertexControlBoundsRulesForCellState(state: mxCellState): MxRectangle {
    try {
      const {
        view, x, y, width, height, ...others
      } = state;
      // @ts-ignore
      const { control: { scale, bounds } } = others;
      const w = bounds.width / scale;
      const h = bounds.height / scale;
      const s = view.scale;
      return new MxRectangle(x + width / 2 - w / 2 * s + 0.5, y + height - h / 2 * s + 7, w * s, h * s);
    } catch (e) {
      return new MxRectangle(0, 0, 100, 100);
    }
  }

  /**
   * @override
   * @param {MxGraphType.mxCell} cell
   * @param {boolean} show
   */
  toggleSubTree(cell: mxCell, show: boolean) {
    const cells: mxCell[] = [];
    // @ts-ignore
    this.traverse(cell, true, (vertex) => {
      if (vertex !== cell) {
        cells.push(vertex);
      }
      // Stops recursion if a collapsed cell is seen
      return vertex === cell || !this.isCellCollapsed(vertex);
    });
    this.toggleCells((show != null) ? show : true, cells, true);
  }

  /**
   * @override
   */
  onNotifyGraphMounted() { }

  /**
   * @override
   */
  onMountComplete() { }

  /**
   * @override
   */
  onUnmountComplete() { }

  /**
   * @override
   * Extends mxGraphLayout to implement a compact tree (Moen) algorithm.
   * This layout is suitable for graphs that have no cycles (trees).
   * Vertices that are not connected to the tree will be ignored by this layout.
   * Checkout {@link 'https://jgraph.github.io/mxgraph/docs/js-api/files/layout/mxCompactTreeLayout-js.html'}
   * for more configuration options
   * @returns {FaultTreeMxGraphLayout}
   */
  // @ts-ignore
  getDefaultLayout(): mxGraphLayout {
    const treeLayout = new FaultTreeMxGraphLayout(this);
    // @ts-ignore
    treeLayout.edgeRouting = false;
    // @ts-ignore
    treeLayout.levelDistance = 30;
    // @ts-ignore
    treeLayout.nodeDistance = 10;
    // @ts-ignore
    return treeLayout;
  }

  /**
   * @param {FaultTreeMxGraphJSON} inputJSON
   * @throws {InvalidJSONFormatError}
   */
  populateFromJSON(inputJSON: FaultTreeMxGraphJSON) {
    new FaultTreeMxGraphDecoder(this, inputJSON, this.commonCause, this.commonCauseFactors).decode();
  }

  /**
   * @override
   * @return {FaultTreeMxGraphJSON}
   */
  toJSON(): FaultTreeMxGraphJSON {
    return new FaultTreeMxGraphEncoder(this).encode();
  }

  /**
   *  @return {@link FaultTreeMxConnectionHandler}
   */
  // protected createConnectionHandler(): FaultTreeMxConnectionHandler {
  //   return new FaultTreeMxConnectionHandler(this);
  // }

  /**
   * @override
   */
  setStyles(): void {
    const edgeStyle = this.getStylesheet().getDefaultEdgeStyle();
    edgeStyle[mxConstants.STYLE_EDGE] = mxEdgeStyle.TopToBottom;
    edgeStyle[mxConstants.STYLE_STROKEWIDTH] = 2;
    edgeStyle[mxConstants.STYLE_STROKECOLOR] = 'rgba(86, 137, 156, 0.75)';
    edgeStyle.endArrow = 'none';
    this.collapsedImage = new MxImage(expandMore, 15, 15);
    this.expandedImage = new MxImage(expandLess, 15, 15);
  }

  public isValidSource(cell: any): boolean {
    return super.isValidSource(cell) && (cell instanceof GateVertex);
  }

  public isValidTarget(cell: any): boolean {
    return super.isValidTarget(cell);
  }

  createStylesheet(): FaultTreeMxStylesheet {
    return new FaultTreeMxStylesheet();
  }

  public createSelectionModel(): FaultTreeMxGraphSelectionModel {
    return new FaultTreeMxGraphSelectionModel(this);
  }

  public addVertexWithUniqueKey(vertex: HCLTreeVertex, parent: mxCell = this.getDefaultParent()): FaultTreeVertex {
    if (vertex instanceof FaultTreeVertex) {
      // @ts-ignore
      return super.addVertexWithUniqueKey(vertex, parent);
    }
    // @ts-ignore
    return null;
  }
}

export default FaultTreeMxGraph;
